import { useEffect, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { CheckCircle2, Store, X } from "lucide-react"
import toast from "react-hot-toast"
import { updateStore } from "../../api/products/post"
import { STORE_CATEGORY_OPTIONS } from "../../utils/storeOptions"

type Props = {
  isOpen: boolean
  store: any | null
  onClose: () => void
  onUpdated: (store: any) => void
}

export default function EditStoreCategoryModal({
  isOpen,
  store,
  onClose,
  onUpdated,
}: Props) {
  const [category, setCategory] = useState("GENERAL")
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isOpen && store) {
      setCategory(String(store.category || store.type || "GENERAL").toUpperCase())
      setError("")
      setSaving(false)
    }
  }, [isOpen, store])

  const handleSubmit = async () => {
    if (!store?._id && !store?.id) return

    const selected = STORE_CATEGORY_OPTIONS.find((option) => option.key === category)
    if (!selected) {
      setError("Catégorie invalide")
      return
    }

    try {
      setSaving(true)
      setError("")
      const updated = await updateStore(String(store._id || store.id), {
        category: selected.key,
        type: selected.legacyType,
      })
      toast.success(`Catégorie mise à jour: ${selected.label}`)
      onUpdated(updated)
      onClose()
    } catch (e: any) {
      setError(e?.message || "Impossible de modifier la catégorie")
    } finally {
      setSaving(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && store && (
        <motion.div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/60 px-3 py-4 backdrop-blur-sm sm:px-4 sm:py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="mx-auto my-auto w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-xl"
            initial={{ scale: 0.96, y: 24 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 24 }}
          >
            <div className="flex items-start justify-between border-b border-slate-100 px-5 py-5 sm:px-6">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                  <Store size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">Modifier la catégorie</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Choisissez le mode de gestion le plus adapté pour {store?.name || "cette boutique"}.
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="rounded-lg p-1 hover:bg-slate-100">
                <X />
              </button>
            </div>

            <div className="space-y-5 px-5 py-5 sm:px-6">
              {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Boutique</p>
                <p className="mt-2 text-base font-semibold text-slate-800">{store?.name || "-"}</p>
                <p className="mt-1 text-sm text-slate-500">{store?.city || "Ville non renseignée"}</p>
              </div>

              <div className="grid gap-3">
                {STORE_CATEGORY_OPTIONS.map((option) => {
                  const active = category === option.key
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setCategory(option.key)}
                      className={`rounded-2xl border p-4 text-left transition ${
                        active
                          ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100"
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-800">{option.label}</p>
                          <p className="mt-1 text-sm text-slate-500">{option.description}</p>
                          <p className="mt-2 text-xs uppercase tracking-[0.14em] text-slate-400">
                            Valeur enregistrée: {option.key}
                          </p>
                        </div>
                        {active && <CheckCircle2 className="mt-0.5 text-blue-600" size={18} />}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-100 px-5 py-4 sm:px-6">
              <button
                onClick={onClose}
                className="rounded-xl border border-slate-200 px-4 py-2 text-slate-700 hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                disabled={saving}
                onClick={handleSubmit}
                className="rounded-xl bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Mise à jour..." : "Enregistrer"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
